import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  FiHome,
  FiUsers,
  FiCreditCard,
  FiActivity,
  FiSettings,
  FiLogOut,
  FiMenu,
  FiX,
  FiShield,
  FiChevronDown,
  FiHeart,
  FiDollarSign,
  FiTool,
} from 'react-icons/fi';
import './AdminLayout.css';

/**
 * Admin Role Labels (UI-ready for Phase 4)
 * Currently all admins have app_admin role
 * Future phases will implement: Super Admin, Finance, Support with different permissions
 *
 * PHASE 3B: UI labels only - no permission enforcement yet
 */
const ADMIN_ROLE_LABELS = {
  app_admin: {
    label: 'App Admin',
    description: 'Full administrative access',
    color: '#6C5CE7',
    icon: FiShield,
  },
  super_admin: {
    label: 'Super Admin',
    description: 'Platform owner access',
    color: '#F64E60',
    icon: FiShield,
  },
  finance: {
    label: 'Finance Admin',
    description: 'Billing and subscription access',
    color: '#1BC5BD',
    icon: FiDollarSign,
  },
  support: {
    label: 'Support Admin',
    description: 'User support access',
    color: '#3699FF',
    icon: FiHeart,
  },
};

/**
 * Get admin role display info
 * Phase 3B: All admins currently show as "App Admin"
 * Phase 4+: Will differentiate based on granular role
 */
const getAdminRoleInfo = (role) => {
  return ADMIN_ROLE_LABELS[role] || ADMIN_ROLE_LABELS.app_admin;
};

/**
 * AdminLayout - Dedicated layout for admin portal
 * ENTERPRISE RULE: Admin UI is separate from user UI
 * - Separate sidebar, topbar, navigation
 * - Serious, calm, executive feel
 * - No playful colors or user-facing CTA language
 */
const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Get role info for current admin
  const roleInfo = getAdminRoleInfo(user?.role);
  const RoleIcon = roleInfo.icon;

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const navItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: FiHome },
    { path: '/admin/users', label: 'Users', icon: FiUsers },
    { path: '/admin/subscriptions', label: 'Subscriptions', icon: FiCreditCard },
    { path: '/admin/activity', label: 'Activity Logs', icon: FiActivity },
    { path: '/admin/maintenance', label: 'Maintenance', icon: FiTool },
    { path: '/admin/settings', label: 'Settings', icon: FiSettings },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="admin-layout">
      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="admin-sidebar-header">
          <FiShield size={24} />
          <span className="admin-brand">Admin Portal</span>
          <button
            className="admin-sidebar-close"
            onClick={() => setSidebarOpen(false)}
          >
            <FiX size={20} />
          </button>
        </div>

        <nav className="admin-nav">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`admin-nav-item ${isActive(item.path) ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
            >
              <item.icon size={18} />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="admin-sidebar-footer">
          <div className="admin-env-badge">
            {process.env.NODE_ENV === 'production' ? 'PRODUCTION' : 'DEVELOPMENT'}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="admin-main">
        {/* Topbar */}
        <header className="admin-topbar">
          <button
            className="admin-menu-toggle"
            onClick={() => setSidebarOpen(true)}
          >
            <FiMenu size={20} />
          </button>

          <div className="admin-topbar-title">
            Hisab OK Hai - Administration
          </div>

          <div className="admin-topbar-actions">
            <div className="admin-user-menu">
              <button
                className="admin-user-button"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
              >
                <div className="admin-user-avatar">
                  {user?.full_name?.charAt(0) || user?.email?.charAt(0) || 'A'}
                </div>
                <span className="admin-user-name">{user?.full_name || user?.email}</span>
                <FiChevronDown size={16} />
              </button>

              {userMenuOpen && (
                <div className="admin-user-dropdown">
                  <div className="admin-user-info">
                    <strong>{user?.full_name || 'Administrator'}</strong>
                    <span>{user?.email}</span>
                    <span
                      className="admin-role-badge"
                      style={{ backgroundColor: `${roleInfo.color}15`, color: roleInfo.color }}
                    >
                      <RoleIcon size={12} style={{ marginRight: 4 }} />
                      {roleInfo.label}
                    </span>
                    <span className="admin-role-description">{roleInfo.description}</span>
                  </div>
                  <div className="admin-dropdown-divider" />
                  <button onClick={handleLogout} className="admin-logout-btn">
                    <FiLogOut size={16} />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="admin-content">
          {children}
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="admin-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
